import logger from './logger';
import storage from './LocalStorageManager';
import { DATA_TYPE, PREFERRED_ICON_TYPE } from '../config/TypeConfig';

/**
 * 默认卡片添加工具类
 * 用于在特定条件下自动添加默认卡片到首页
 */
class DefaultAddCardUtils {
  // 存储键名常量
  static KEYS = {
    HOT_SEARCH_ADDED: 'hotSearchAdded',
    // 后续可以添加其他卡片的标记
    // CUSTOM_CARD_ADDED: 'customCardAdded',
  };

  /**
   * 生成复合ID的工具函数
   * @param {Object} app - 应用数据
   * @returns {string} 复合ID
   */
  static generateComplexId(app) {
    return `${app.url || ''}_${app.name || ''}_${app.id || Date.now()}_${Math.random().toString(36).substring(2, 8)}`;
  }

  /**
   * 创建热搜卡片数据
   * @returns {Object} 热搜卡片数据
   */
  static createHotSearchCard() {
    const timestamp = Date.now();
    return {
      id: `hotsearch_${timestamp}`,
      name: '热搜',
      title: '热搜',
      url: '#',
      dataType: DATA_TYPE.HOT_SEARCH,
      preferredIconType: PREFERRED_ICON_TYPE.TEXT,
      textIcon: '热搜',
      bgColor: 'white',
      iconColor: '#000000',
      complexId: this.generateComplexId({ 
        id: `hotsearch_${timestamp}`, 
        name: '热搜' 
      })
    };
  }

  /**
   * 检查是否需要添加热搜卡片
   * @param {string} categoryId - 分类ID
   * @param {Array} apps - 应用列表
   * @returns {boolean} 是否需要添加
   */
  static shouldAddHotSearchCard(categoryId, apps) {
    // 只在首页添加
    if (categoryId !== 'home') {
      return false;
    }

    // 检查是否已经添加过
    const hotSearchAdded = storage.getItem(this.KEYS.HOT_SEARCH_ADDED) === 'true';
    if (hotSearchAdded) {
      return false;
    }

    // 检查应用列表中是否已经有热搜卡片
    const hasHotSearch = apps.some(app => app.dataType === DATA_TYPE.HOT_SEARCH);
    if (hasHotSearch) {
      return false;
    }

    return true;
  }

  /**
   * 添加热搜卡片到应用列表
   * @param {Array} apps - 当前应用列表
   * @returns {Array} 更新后的应用列表
   */
  static addHotSearchCard(apps) {
    const hotSearchCard = this.createHotSearchCard();
    const updatedApps = [hotSearchCard, ...apps];
    
    // 设置标记，表示已经添加过
    storage.setItem(this.KEYS.HOT_SEARCH_ADDED, 'true');
    
    logger.log('[DefaultAddCardUtils] 自动添加热搜卡片到首页', {
      cardId: hotSearchCard.id,
      totalApps: updatedApps.length
    });
    
    return updatedApps;
  }

  /**
   * 检查并添加所有默认卡片
   * @param {string} categoryId - 分类ID
   * @param {Array} apps - 当前应用列表
   * @returns {Object} 结果对象
   */
  static checkAndAddDefaultCards(categoryId, apps) {
    let updatedApps = [...apps];
    let hasChanges = false;
    const addedCards = [];

    // 检查并添加热搜卡片
    if (this.shouldAddHotSearchCard(categoryId, updatedApps)) {
      updatedApps = this.addHotSearchCard(updatedApps);
      hasChanges = true;
      addedCards.push('hotSearch');
    }

    // 后续可以添加其他默认卡片的检查逻辑
    // if (this.shouldAddCustomCard(categoryId, updatedApps)) {
    //   updatedApps = this.addCustomCard(updatedApps);
    //   hasChanges = true;
    //   addedCards.push('customCard');
    // }

    return {
      apps: updatedApps,
      hasChanges,
      addedCards
    };
  }

  /**
   * 重置所有默认卡片标记
   * 用于测试或重置功能
   */
  static resetAllMarks() {
    Object.values(this.KEYS).forEach(key => {
      storage.removeItem(key);
    });
    logger.log('[DefaultAddCardUtils] 已重置所有默认卡片标记');
  }

  /**
   * 获取所有默认卡片标记状态
   * @returns {Object} 标记状态对象
   */
  static getAllMarksStatus() {
    const status = {};
    Object.entries(this.KEYS).forEach(([name, key]) => {
      status[name] = storage.getItem(key) === 'true';
    });
    return status;
  }

  /**
   * 手动设置某个卡片的添加状态
   * @param {string} cardType - 卡片类型
   * @param {boolean} added - 是否已添加
   */
  static setCardMark(cardType, added) {
    const keyMap = {
      'hotSearch': this.KEYS.HOT_SEARCH_ADDED,
      // 后续可以添加其他卡片的映射
    };

    const key = keyMap[cardType];
    if (key) {
      storage.setItem(key, added.toString());
      logger.log(`[DefaultAddCardUtils] 设置 ${cardType} 卡片标记为: ${added}`);
    } else {
      logger.warn(`[DefaultAddCardUtils] 未知的卡片类型: ${cardType}`);
    }
  }
}

export default DefaultAddCardUtils;
